import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { createBlendy } from "blendy";
import { isMobileOrReducedMotion } from "@/utils/device";

const BlendyContext = createContext({
  blendy: null,
  enabled: false,
  update: () => {},
});

export function BlendyProvider({ children }) {
  const [blendy, setBlendy] = useState(null);
  const enabled = !isMobileOrReducedMotion();

  useEffect(() => {
    if (!enabled) {
      setBlendy(null);
      return;
    }

    const instance = createBlendy({ animation: "dynamic" });
    setBlendy(instance);

    return () => {
      if (typeof instance?.destroy === "function") {
        instance.destroy();
      }
      setBlendy(null);
    };
  }, [enabled]);

  useEffect(() => {
    if (!enabled || !blendy || typeof window === "undefined") return;

    const observer = new MutationObserver(() => {
      blendy.update();
    });

    observer.observe(document.body, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, [enabled, blendy]);

  const value = useMemo(
    () => ({
      blendy,
      enabled,
      update: () => {
        if (!enabled || !blendy) return;
        blendy.update();
      },
    }),
    [blendy, enabled]
  );

  return <BlendyContext.Provider value={value}>{children}</BlendyContext.Provider>;
}

export function useBlendy() {
  return useContext(BlendyContext);
}
